import { useCallback, useState } from "react";
import {
  faEnvelope,
  faMessage,
  faRightFromBracket,
  faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Menu, ScrollArea, useMantineTheme } from "@mantine/core";
import { UserButton } from "components/UI/LeftMenu/UserButton";
import { FEEDBACK_EMAIL } from "config/constants";
import { useAuth } from "hooks/useAuth";
import { useGenericModals } from "hooks/useGenericModals";
import { useTranslation } from "react-i18next";
import { getMailToLink } from "utils/helpers/getMailToLink";

import { DownloadTrackbook } from "./MenuItems/DownloadTrackbook";
import { ImportGPXMenuItem } from "./MenuItems/ImportGPXMenuItem";
import { TabMenu } from "./TabMenu";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import scrollAreaClasses from "components/UI/ScrollArea.module.css";

const ITEM_HEIGHT = 34;
export const USER_MENU_ICON_WIDTH = 15;

export const AuthenticatedUserMenu = () => {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const isSmallViewport = useIsSmallViewport();
  const theme = useMantineTheme();

  const { t } = useTranslation();
  const { openSettingsModal } = useGenericModals();

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSignOutClick = () => {
    void signOut();
    close();
  };

  const handleContactUsClick = () => {
    window.location.href = getMailToLink(FEEDBACK_EMAIL, "Trackbook feedback");
    close();
  };

  const handleSendFeedbackClick = () => {
    close();
    window.open("https://forms.gle/TruHuHm1f1JXBbxNA", "_blank");
  };

  return (
    <>
      <Box p="xs" style={{ padding: isSmallViewport ? 4 : 0 }}>
        <Menu
          closeOnItemClick
          closeOnClickOutside
          onChange={setIsOpen}
          styles={{
            item: { width: "100%", height: ITEM_HEIGHT },
          }}
          transitionProps={{ transition: "fade" }}
          position="bottom-end"
          opened={isOpen}
          shadow="lg"
        >
          <Menu.Target>
            <UserButton email={user?.email || ""} />
          </Menu.Target>

          <Menu.Dropdown>
            <ScrollArea
              style={{ maxWidth: "100%" }}
              classNames={{ thumb: scrollAreaClasses.thumb }}
            >
              <Box style={{ overflow: "hidden" }}>
                <Menu.Item
                  onClick={handleContactUsClick}
                  leftSection={
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      color={theme.colors.gray[6]}
                    />
                  }
                >
                  {t("dropdown_menu.contact_us_label")}
                </Menu.Item>

                <Menu.Item
                  onClick={handleSendFeedbackClick}
                  leftSection={
                    <FontAwesomeIcon
                      icon={faMessage}
                      color={theme.colors.gray[6]}
                    />
                  }
                >
                  {t("dropdown_menu.send_feedback_label")}
                </Menu.Item>
                <Menu.Divider />
                <DownloadTrackbook iconWidth={USER_MENU_ICON_WIDTH} />
                <Menu.Divider />
                <ImportGPXMenuItem />
                <Menu.Item
                  component="div"
                  onClick={openSettingsModal}
                  leftSection={
                    <FontAwesomeIcon
                      icon={faSliders}
                      color={theme.colors.gray[6]}
                    />
                  }
                >
                  {t("dropdown_menu.account_settings_button_label")}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  onClick={handleSignOutClick}
                  leftSection={
                    <FontAwesomeIcon
                      icon={faRightFromBracket}
                      color={theme.colors.gray[6]}
                    />
                  }
                >
                  {t("dropdown_menu.logout_label")}
                </Menu.Item>
              </Box>
            </ScrollArea>
          </Menu.Dropdown>
        </Menu>
      </Box>
      <TabMenu />
    </>
  );
};
